import React, { useState, useEffect, useRef } from 'react';
import { Input, Button, Spin, Select, Collapse, message, List } from 'antd';
import { SendOutlined, PlusOutlined, MessageOutlined } from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useLanguage } from '../../contexts/LanguageContext';
import { apiService } from '../WorkflowDesigner/services/apiService';
import './OnboardingUsageTutorialChat.css';

const { TextArea } = Input;

const OnboardingUsageTutorialChat = () => {
  const { t } = useLanguage();
  const messagesEndRef = useRef(null);
  const [conversations, setConversations] = useState([]);
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingConversations, setLoadingConversations] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [aiProviders, setAiProviders] = useState([]);
  const [selectedProviderKey, setSelectedProviderKey] = useState('');
  const [loadingProviders, setLoadingProviders] = useState(false);

  useEffect(() => {
    loadProviders();
    loadConversations();
  }, []);

  useEffect(() => {
    if (selectedConversationId) {
      loadHistory(selectedConversationId);
    } else {
      setMessages([]);
    }
  }, [selectedConversationId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadProviders = async () => {
    setLoadingProviders(true);
    try {
      const list = await apiService.fetchAiProviders('AI');
      const providers = Array.isArray(list) ? list : [];
      setAiProviders(providers);
      const active = providers.find(p => p.active);
      setSelectedProviderKey(prev => prev || (active || providers[0])?.providerKey || '');
    } catch (e) {
      console.error('Load AI providers failed', e);
    } finally {
      setLoadingProviders(false);
    }
  };

  const loadConversations = async () => {
    setLoadingConversations(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/onboarding-usage-tutorial/conversations', {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (!res.ok) throw new Error('Failed to load conversations');
      const data = await res.json();
      setConversations(Array.isArray(data) ? data : []);
    } catch (e) {
      console.warn('Load conversations failed', e);
      setConversations([]);
    } finally {
      setLoadingConversations(false);
    }
  };

  const loadHistory = async (conversationId) => {
    if (!conversationId) return;
    setLoadingHistory(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/onboarding-usage-tutorial/chat?conversationId=${conversationId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (!res.ok) throw new Error('Failed to load history');
      const data = await res.json();
      setMessages(Array.isArray(data) ? data : []);
    } catch (e) {
      console.warn('Load chat history failed', e);
      setMessages([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleNewChat = () => {
    setSelectedConversationId(null);
    setMessages([]);
    setInput('');
  };

  const handleSend = async () => {
    const text = (input || '').trim();
    if (!text) return;
    if (aiProviders.length > 0 && !selectedProviderKey) {
      message.warning(t('onboarding.usageTutorial.selectProvider') || '請選擇 AI 供應商');
      return;
    }

    setSending(true);
    setInput('');
    try {
      const token = localStorage.getItem('token');
      const currentRoute = typeof window !== 'undefined' ? window.location?.pathname : '';
      const res = await fetch('/api/onboarding-usage-tutorial/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          message: text,
          providerKey: selectedProviderKey || '',
          currentRoute: currentRoute || undefined,
          conversationId: selectedConversationId || undefined
        })
      });
      const result = await res.json();
      if (!res.ok) {
        message.error(result?.error || '發送失敗');
        setInput(text);
        return;
      }
      if (result.isNewConversation && result.conversationId) {
        setConversations(prev => [
          { id: result.conversationId, subject: result.userMessage?.content?.slice(0, 80) || text, createdAt: result.userMessage?.createdAt },
          ...prev
        ]);
        setSelectedConversationId(result.conversationId);
      }
      setMessages(prev => [
        ...prev,
        { id: result.userMessage?.id, role: 'user', content: text, messageIndex: result.userMessage?.messageIndex, createdAt: result.userMessage?.createdAt },
        { id: result.assistantMessage?.id, role: 'assistant', content: result.reply || '', messageIndex: result.assistantMessage?.messageIndex, createdAt: result.assistantMessage?.createdAt }
      ]);
    } catch (e) {
      console.error('Send onboarding chat failed', e);
      message.error(t('onboarding.usageTutorial.sendFailed') || '發送失敗');
      setInput(text);
    } finally {
      setSending(false);
    }
  };

  const role = (msg) => msg.role || msg.Role;
  const content = (msg) => msg.content || msg.Content;

  const formatDate = (d) => {
    if (!d) return '';
    const date = typeof d === 'string' ? new Date(d) : d;
    const now = new Date();
    const sameDay = date.toDateString() === now.toDateString();
    return sameDay ? date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : date.toLocaleDateString();
  };

  return (
    <div className="onboarding-chat-layout">
      {/* 左側：對話列表 */}
      <div className="onboarding-chat-sidebar">
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleNewChat}
          className="onboarding-chat-new-btn"
          block
        >
          {t('onboarding.usageTutorial.newChat') || '新對話'}
        </Button>
        {loadingConversations ? (
          <div className="onboarding-chat-sidebar-loading">
            <Spin size="small" />
          </div>
        ) : (
          <List
            className="onboarding-chat-conversation-list"
            dataSource={conversations}
            renderItem={(item) => (
              <List.Item
                className={`onboarding-chat-conversation-item ${selectedConversationId === item.id ? 'selected' : ''}`}
                onClick={() => setSelectedConversationId(item.id)}
              >
                <MessageOutlined style={{ marginRight: 8, flexShrink: 0 }} />
                <div className="onboarding-chat-conversation-subject" title={item.subject}>
                  {item.subject || (t('onboarding.usageTutorial.newChat') || '新對話')}
                </div>
                <div className="onboarding-chat-conversation-date">{formatDate(item.createdAt)}</div>
              </List.Item>
            )}
          />
        )}
      </div>

      {/* 右側：對話區 */}
      <div className="onboarding-chat-main">
        <div
          className="onboarding-chat-messages"
        >
          {!selectedConversationId && !messages.length && (
            <div className="onboarding-chat-empty">
              <MessageOutlined style={{ fontSize: 48, color: '#d9d9d9', marginBottom: 16 }} />
              <div className="onboarding-chat-empty-hint">
                {t('onboarding.usageTutorial.selectConversation') || '選擇左側對話或建立新對話'}
              </div>
              <div className="onboarding-chat-empty-sub">在下方輸入問題即可開始新對話</div>
            </div>
          )}
          {selectedConversationId && loadingHistory && (
            <div className="onboarding-chat-loading">
              <Spin size="small" />
            </div>
          )}
          {selectedConversationId && !loadingHistory && messages.length === 0 && (
            <div className="onboarding-chat-empty">
              <div className="onboarding-chat-empty-hint">
                {t('onboarding.usageTutorial.emptyHint') || '輸入問題，我將根據使用說明為您解答'}
              </div>
            </div>
          )}
          {selectedConversationId && !loadingHistory && messages.map((msg) => {
            const r = role(msg);
            const isUser = r === 'user';
            return (
              <div
                key={msg.id || `${msg.messageIndex || msg.MessageIndex}-${r}`}
                className={`onboarding-chat-bubble-wrap ${isUser ? 'user' : 'assistant'}`}
              >
                <div className="onboarding-chat-bubble">
                  {isUser ? (
                    <div className="onboarding-chat-bubble-text">{content(msg)}</div>
                  ) : (
                    <div className="onboarding-chat-bubble-md">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{content(msg)}</ReactMarkdown>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          <div ref={messagesEndRef} />
        </div>

        {aiProviders.length === 0 && !loadingProviders && (
          <div className="onboarding-chat-no-provider">
            {t('onboarding.usageTutorial.noProvider') || '請先在 API 供應商中啟用至少一個 AI'}
          </div>
        )}

        <div className="onboarding-chat-input-wrap">
          <div className="onboarding-chat-input-inner">
            <TextArea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t('onboarding.usageTutorial.placeholder') || '輸入您的問題...'}
              rows={2}
              className="onboarding-chat-textarea"
              disabled={sending}
              onPressEnter={(e) => { if (!e.shiftKey) { e.preventDefault(); handleSend(); } }}
            />
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={handleSend}
              loading={sending}
              disabled={!input.trim() || (aiProviders.length > 0 && !selectedProviderKey)}
              className="onboarding-chat-send-btn"
            />
          </div>
          <Collapse
            size="small"
            className="onboarding-chat-advanced"
            items={[
              {
                key: '1',
                label: t('onboarding.usageTutorial.advancedOptions') || '進階選項',
                children: (
                  <div>
                    <label style={{ fontSize: 12 }}>{t('onboarding.usageTutorial.aiProvider') || 'AI 供應商'}</label>
                    <Select
                      value={selectedProviderKey || undefined}
                      onChange={setSelectedProviderKey}
                      placeholder={t('onboarding.usageTutorial.selectProvider') || '請選擇'}
                      style={{ width: '100%' }}
                      size="small"
                      loading={loadingProviders}
                      allowClear
                      options={aiProviders.map(p => ({
                        value: p.providerKey,
                        label: `${p.displayName}${p.active ? '' : ` (${t('workflowDesigner.aiProviderInactive') || '未啟用'})`}`,
                        disabled: p.active === false
                      }))}
                    />
                  </div>
                )
              }
            ]}
          />
        </div>
      </div>
    </div>
  );
};

export default OnboardingUsageTutorialChat;
